import React from 'react';
import { ChartPane, chartContext } from '../../ui/chart/ChartPane';
import XAxis from '../../ui/chart/XAxis';
import LineSeries from '../../ui/chart/LineSeries';
import { Link } from '@material-ui/core';
import { ZoomBar } from '../../ui/chart/ZoomBar';
import { TimeRange, DataRange, ChartSeriesV2 } from '../../ui/chart/types';
import { makeStyles, Theme } from '@material-ui/core';

type ClearPosition = 'top' | 'bottom';

interface ChartZoomControlProps {
  fullRanges: DataRange[];
  data?: Record<string, ChartSeriesV2[]>;
  zoomRange?: TimeRange;
  onZoomChange: (range: TimeRange | undefined) => void;
  height?: number;
  clearPosition?: ClearPosition;
  backgroundColor?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  zoomControlFooter: (props: { clearPosition: ClearPosition }) => ({
    textAlign: 'right',
    fontSize: 12,
    ...(props.clearPosition === 'top' && {
      position: 'absolute',
      top: -24,
      right: 0,
    }),
    ...(props.clearPosition === 'bottom' && {
      marginTop: 18,
    }),
    height: 8,
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
    '&.visible': {
      opacity: 1,
    },
  }),
}));

export const ChartZoomControl: React.FC<ChartZoomControlProps> = ({
  fullRanges,
  data,
  zoomRange,
  onZoomChange,
  height = 60,
  clearPosition = 'bottom',
  backgroundColor = '#f0f0f0',
}) => {
  const classes = useStyles({ clearPosition });

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {clearPosition === 'top' && (
        <div className={`${classes.zoomControlFooter} ${zoomRange ? 'visible' : ''}`}>
          {zoomRange ? (
            <Link onClick={() => onZoomChange(undefined)} style={{ cursor: 'pointer' }}>
              Clear Zoom
            </Link>
          ) : null}
        </div>
      )}
      <ChartPane
        height={height}
        range={fullRanges[0]}
        altRanges={fullRanges.length > 1 ? fullRanges.slice(1) : undefined}
        style={{ backgroundColor }}
        margins={{
          top: 8,
          bottom: 8,
          left: 0,
          right: 0,
        }}
      >
        <chartContext.Consumer>
          {(context) => (
            <>
              {data &&
                Object.values(data).map((group, i) => {
                  const yScale =
                    i === 0 ? context.yScale : context.altYScales && context.altYScales[i - 1];
                  return yScale ? (
                    <LineSeries key={i} {...context} yScale={yScale} data={group || []} />
                  ) : null;
                })}

              <XAxis {...context} inset={true} offset={-1} />

              <ZoomBar
                {...context}
                zoomRange={zoomRange}
                onZoom={(newZoomRange) => onZoomChange(newZoomRange)}
                onResetZoom={() => onZoomChange(undefined)}
              />
            </>
          )}
        </chartContext.Consumer>
      </ChartPane>
      {clearPosition === 'bottom' && (
        <div className={`${classes.zoomControlFooter} ${zoomRange ? 'visible' : ''}`}>
          {zoomRange ? (
            <Link onClick={() => onZoomChange(undefined)} style={{ cursor: 'pointer' }}>
              Clear Zoom
            </Link>
          ) : null}
        </div>
      )}
    </div>
  );
};
